import { notifications, type NotificationData } from '@mantine/notifications';
import React, { ReactNode } from 'react';
import { primaryDarkest, redDarkest, yellowDarkest } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { AUTO_CLOSE_TIMEOUT, DEFAULT_POSITION } from './notifications.constants';
import {
  NotificationType,
  type NotificationConfig,
  type NotificationMethod,
  type NotificationModalHandler,
} from './notifications.type';

/**
 * Icons to display for each notification type
 * Maps notification types to their corresponding icon components
 */
const notificationIcons: Record<NotificationType, ReactNode> = {
  [NotificationType.Success]: <Icon name="task_alt" variant="outlined" size={20} color={primaryDarkest} />,
  [NotificationType.Warning]: <Icon name="warning_amber" variant="outlined" size={20} color={yellowDarkest} />,
  [NotificationType.Error]: <Icon name="error_outline" variant="outlined" size={20} color={redDarkest} />,
};

/**
 * Handler function for showing notification content in a modal.
 * Used when notification text is truncated.
 *
 * This needs to be a global variable because:
 * 1. It's set once during app initialization via setModalHandler() when NotificationProvider mounts
 * 2. It's accessed by createNotification() which is called from multiple places
 * 3. We can't pass the handler through normal props/parameters since notifications can be shown
 *    from anywhere in the app, including non-React code
 */
let modalHandler: NotificationModalHandler | null = null;

/**
 * Sets the handler function for showing notification content in a modal
 *
 * @param {NotificationModalHandler} handler - Function to handle showing notification content in modal
 * @returns {void}
 */
const setModalHandler = (handler: NotificationModalHandler): void => {
  modalHandler = handler;
};

/**
 * Check if text element is truncated (has ellipsis)
 *
 * @param {HTMLElement} element - The element to check if it's truncated
 * @returns {boolean} True if the element is truncated, false otherwise
 */
const isTextTruncated = (element: HTMLElement): boolean => {
  return element.scrollWidth > element.clientWidth;
};

/**
 * Handle notification click with smart modal detection
 *
 * @param {NotificationConfig} config - The notification configuration
 * @param {NotificationModalHandler} modalHandler - The modal handler function
 * @returns {Function} The click handler function
 */
const createNotificationClickHandler = (
  config: NotificationConfig,
  modalHandler?: NotificationModalHandler,
): ((event: React.MouseEvent<HTMLDivElement>) => void) => {
  return (event: React.MouseEvent<HTMLDivElement>): void => {
    // Don't open modal if clicking on close button
    const target = event.target as HTMLElement;
    if (target.closest('.mantine-Notification-closeButton')) {
      return;
    }

    // Check if text is truncated
    const notificationElement = target.closest('.mantine-Notification-root');
    const textElement = notificationElement?.querySelector('.mantine-Notification-description') as HTMLElement;

    if (textElement && isTextTruncated(textElement) && modalHandler) {
      modalHandler(config.title, config.message, config.type);
    }
  };
};

/**
 * Core notification creation function with unified logic
 *
 * @param {NotificationConfig} config - The notification configuration
 * @param {NotificationModalHandler} modalHandler - The modal handler function
 * @returns {string} The unique ID of the created notification
 */
const createNotification = (config: NotificationConfig, modalHandler?: NotificationModalHandler): string => {
  const notificationId = config.id || `notification-${Date.now()}-${Math.random()}`;

  notifications.show({
    id: notificationId,
    title: config.title,
    message: config.message,
    icon: notificationIcons[config.type],
    autoClose: config.autoClose !== undefined ? config.autoClose : AUTO_CLOSE_TIMEOUT,
    withCloseButton: config.withCloseButton !== undefined ? config.withCloseButton : true,
    position: (config.position || DEFAULT_POSITION) as NotificationData['position'],
    className: `bloom-notification bloom-notification-${config.type}`,
    style: {
      cursor: 'pointer',
    },
    onClick: createNotificationClickHandler(config, modalHandler),
  });

  return notificationId;
};

/**
 * Factory function to create notification methods (showSuccess, showWarning, showError)
 * This avoids duplication between the hook and global utility implementations
 *
 * @param {Function} showNotification - The function to show a notification
 * @returns {Object} An object with the showSuccess, showWarning, and showError methods
 */
const createNotificationMethods = (
  showNotification: (config: NotificationConfig) => string,
): { showSuccess: NotificationMethod; showWarning: NotificationMethod; showError: NotificationMethod } => {
  const showSuccess = (message: string, title?: string, options?: Partial<NotificationConfig>) => {
    return showNotification({
      message,
      title,
      type: NotificationType.Success,
      ...options,
    });
  };

  const showWarning = (message: string, title?: string, options?: Partial<NotificationConfig>) => {
    return showNotification({
      message,
      title,
      type: NotificationType.Warning,
      ...options,
    });
  };

  const showError = (message: string, title?: string, options?: Partial<NotificationConfig>) => {
    return showNotification({
      message,
      title,
      type: NotificationType.Error,
      ...options,
    });
  };

  return {
    showSuccess,
    showWarning,
    showError,
  };
};

export {
  createNotification,
  createNotificationClickHandler,
  createNotificationMethods,
  isTextTruncated,
  modalHandler,
  notificationIcons,
  setModalHandler,
};
